import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UploadDocumentsModal } from "@/features/admin/contacts/components/UploadDocumentsModal";
import { quotesService } from "@/api/services/quotes.service";
import { quoteRequestsService } from "@/api/services/quote-requests.service";
import { documentsService } from "@/api/services/documents.service";
import { DateInput, useToast, CommencementDateInput } from "@/components/common";
import { searchSuburbsAsync } from "@/data/australian-suburbs";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import styles from "./ClientQuoteDetail.module.css";
const TABS = [
    { id: "site_particulars", label: "Site Particulars" },
    { id: "job_description", label: "Job Description" },
    { id: "documents", label: "Documents" },
];
/** Map API category (quoteCategory, siteCategory, etc.) to JobDescriptionDocCategory for display. */
const mapRawCategoryToDocCategory = (rawCategory) => {
    const cat = String(rawCategory ?? "").trim().toLowerCase();
    if (cat === "permits")
        return "permits";
    if (cat === "plans")
        return "plans";
    return "other";
};
/** Map API Document to JobDescriptionDocument for display. Prefer quoteCategory for quote documents. */
const mapDocumentToJobDescriptionDoc = (doc) => {
    const rawCategory = doc.quoteCategory ??
        doc.jobCategory ??
        doc.siteCategory ??
        "other";
    return {
        id: doc.id,
        name: doc.fileName || doc.name,
        category: mapRawCategoryToDocCategory(rawCategory),
        date: doc.createdAt,
        uploadedBy: doc.uploadedByName || "Unknown",
    };
};
const JOB_DESCRIPTION_FILTERS = [
    { id: "all", label: "All" },
    { id: "permits", label: "Permits" },
    { id: "plans", label: "Plans" },
    { id: "other", label: "Other" },
];
const formatDocDate = (value) => formatDateOnlyDisplay(value);
const getJobDescriptionFilterLabel = (id) => JOB_DESCRIPTION_FILTERS.find((filter) => filter.id === id)?.label ?? id;
const AUSTRALIAN_STATES = [
    { value: "NSW", label: "NSW" },
    { value: "VIC", label: "VIC" },
    { value: "QLD", label: "QLD" },
    { value: "SA", label: "SA" },
    { value: "WA", label: "WA" },
    { value: "TAS", label: "TAS" },
    { value: "NT", label: "NT" },
    { value: "ACT", label: "ACT" },
];
const BUILDING_TYPES = [
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "RETAIL", label: "Retail" },
    { value: "HOSPITALITY", label: "Hospitality" },
    { value: "INDUSTRIAL", label: "Industrial" },
];
export const ClientQuoteDetail = () => {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const { error: showError, success: showSuccess } = useToast();
    // Quote data state
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // UI state
    const [activeTab, setActiveTab] = useState("site_particulars");
    const [defitType, setDefitType] = useState("full");
    const [siteInspectionAvailable, setSiteInspectionAvailable] = useState(true);
    const [permitRequired, setPermitRequired] = useState(true);
    // Site Particulars state
    const [location, setLocation] = useState("");
    const [suburb, setSuburb] = useState("");
    const [state, setState] = useState("");
    const [postcode, setPostcode] = useState("");
    const [siteName, setSiteName] = useState("");
    const [tenancyName, setTenancyName] = useState("");
    const [tenancyNumber, setTenancyNumber] = useState("");
    const [buildingType, setBuildingType] = useState("");
    const [squareMeterage, setSquareMeterage] = useState("");
    const [scopeOfWorks, setScopeOfWorks] = useState("");
    // Suburb dropdown state
    const [suburbSearchTerm, setSuburbSearchTerm] = useState("");
    const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
    const [filteredSuburbs, setFilteredSuburbs] = useState([]);
    const suburbDropdownRef = useRef(null);
    // Job Description states
    const [commencementDate, setCommencementDate] = useState("");
    const [commencementTime, setCommencementTime] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [completionTime, setCompletionTime] = useState("");
    const [projectDuration, setProjectDuration] = useState("");
    const [budgetOption, setBudgetOption] = useState("Other");
    const [customBudget, setCustomBudget] = useState("");
    const [jobDescriptionFilter, setJobDescriptionFilter] = useState("all");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [isSavingDocuments, setIsSavingDocuments] = useState(false);
    const fetchDocuments = useCallback(async () => {
        if (!quoteId || !quote)
            return;
        setDocumentsLoading(true);
        try {
            const response = await documentsService.getDocumentsByQuote(quoteId);
            const docs = Array.isArray(response.data) ? response.data : [];
            setDocuments(docs.map(mapDocumentToJobDescriptionDoc));
        }
        catch (err) {
            console.error("Error fetching documents:", err);
            showError("Failed to load documents.");
        }
        finally {
            setDocumentsLoading(false);
        }
    }, [quoteId, quote, showError]);
    useEffect(() => {
        if (quote && quoteId && activeTab === "documents") {
            fetchDocuments();
        }
    }, [quote, quoteId, activeTab, fetchDocuments]);
    const filteredJobDescriptionDocs = useMemo(() => {
        let filtered = documents;
        // Apply category filter
        if (jobDescriptionFilter !== "all") {
            filtered = filtered.filter((doc) => doc.category === jobDescriptionFilter);
        }
        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((doc) => doc.name.toLowerCase().includes(searchLower));
        }
        return filtered;
    }, [jobDescriptionFilter, searchTerm, documents]);
    // Filter suburbs based on search term (fallback to Matthew Proctor if not in local list)
    useEffect(() => {
        if (suburbSearchTerm.trim()) {
            searchSuburbsAsync(suburbSearchTerm).then((results) => {
                setFilteredSuburbs(results.slice(0, 50));
            });
        }
        else {
            setFilteredSuburbs([]);
        }
    }, [suburbSearchTerm]);
    // Handle outside clicks for suburb dropdown
    useEffect(() => {
        if (!showSuburbDropdown)
            return;
        const container = suburbDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && !container.contains(target)) {
                setShowSuburbDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowSuburbDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showSuburbDropdown]);
    // Suburb handlers
    const handleSuburbSelect = (selectedSuburb) => {
        setSuburbSearchTerm(selectedSuburb.name);
        setSuburb(selectedSuburb.name);
        setState(selectedSuburb.state);
        setPostcode(selectedSuburb.postcode);
        setShowSuburbDropdown(false);
    };
    const handleClearSuburb = () => {
        setSuburbSearchTerm("");
        setSuburb("");
        setState("");
        setPostcode("");
        setFilteredSuburbs([]);
    };
    // Number-only validation handler
    const handleNumberInput = (e, setValue) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, "");
        setValue(numericValue);
    };
    // Fetch quote data from API
    useEffect(() => {
        const fetchQuote = async () => {
            if (!quoteId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const response = await quotesService.getQuote(quoteId);
                const quoteData = response.data;
                setQuote(quoteData);
                // Load from quote request when available (client-initiated quotes)
                let loadedFromRequest = false;
                if (quoteData.quoteRequestId) {
                    try {
                        const request = await quoteRequestsService.getQuoteRequest(quoteData.quoteRequestId);
                        if (request.location)
                            setLocation(request.location);
                        if (request.suburb) {
                            setSuburb(request.suburb);
                            setSuburbSearchTerm(request.suburb);
                        }
                        if (request.state)
                            setState(request.state);
                        if (request.postcode)
                            setPostcode(request.postcode);
                        if (request.siteName)
                            setSiteName(request.siteName);
                        if (request.tenancyName)
                            setTenancyName(request.tenancyName);
                        if (request.tenancyNumber)
                            setTenancyNumber(request.tenancyNumber);
                        if (request.buildingType)
                            setBuildingType(request.buildingType);
                        if (request.squareMeterage)
                            setSquareMeterage(request.squareMeterage);
                        if (request.scopeOfWorks)
                            setScopeOfWorks(request.scopeOfWorks);
                        if (request.defitType)
                            setDefitType(request.defitType);
                        if (typeof request.siteInspectionAvailable === "boolean")
                            setSiteInspectionAvailable(request.siteInspectionAvailable);
                        if (typeof request.permitRequired === "boolean")
                            setPermitRequired(request.permitRequired);
                        if (request.commencementDate) {
                            const date = new Date(request.commencementDate);
                            setCommencementDate(date.toISOString().split("T")[0]);
                        }
                        if (request.commencementTime)
                            setCommencementTime(request.commencementTime);
                        if (request.completionDate) {
                            const date = new Date(request.completionDate);
                            setCompletionDate(date.toISOString().split("T")[0]);
                        }
                        if (request.completionTime)
                            setCompletionTime(request.completionTime);
                        if (request.projectDuration)
                            setProjectDuration(request.projectDuration);
                        if (request.budgetOption)
                            setBudgetOption(request.budgetOption);
                        if (request.customBudget)
                            setCustomBudget(request.customBudget);
                        loadedFromRequest = true;
                    }
                    catch (requestErr) {
                        console.error("Error loading quote request:", requestErr);
                    }
                }
                // When no quoteRequestId or request load failed: use siteParticulars
                if (!loadedFromRequest && quoteData.siteParticulars) {
                    try {
                        const siteParticulars = JSON.parse(quoteData.siteParticulars);
                        if (siteParticulars.location)
                            setLocation(siteParticulars.location);
                        if (siteParticulars.suburb) {
                            setSuburb(siteParticulars.suburb);
                            setSuburbSearchTerm(siteParticulars.suburb);
                        }
                        if (siteParticulars.state)
                            setState(siteParticulars.state);
                        if (siteParticulars.postcode)
                            setPostcode(siteParticulars.postcode);
                        if (siteParticulars.siteName)
                            setSiteName(siteParticulars.siteName);
                        if (siteParticulars.tenancyName)
                            setTenancyName(siteParticulars.tenancyName);
                        if (siteParticulars.tenancyNumber)
                            setTenancyNumber(siteParticulars.tenancyNumber);
                        if (siteParticulars.buildingType)
                            setBuildingType(siteParticulars.buildingType);
                        if (siteParticulars.squareMeterage)
                            setSquareMeterage(siteParticulars.squareMeterage);
                        if (typeof siteParticulars.siteInspectionAvailable === "boolean") {
                            setSiteInspectionAvailable(siteParticulars.siteInspectionAvailable);
                        }
                        if (typeof siteParticulars.permitRequired === "boolean") {
                            setPermitRequired(siteParticulars.permitRequired);
                        }
                        if (siteParticulars.defitType)
                            setDefitType(siteParticulars.defitType);
                    }
                    catch (parseError) {
                        console.error("Error parsing siteParticulars:", parseError);
                    }
                }
                // Set defitType from quote if not set from request
                if (quoteData.defitType) {
                    setDefitType(quoteData.defitType);
                }
                // Set scope of works from quote if not set from request
                if (!loadedFromRequest) {
                    if (quoteData.description) {
                        setScopeOfWorks(quoteData.description);
                    }
                    else if (quoteData.jobDescription) {
                        setScopeOfWorks(quoteData.jobDescription);
                    }
                }
                // Parse and set commencement date/time (convert to YYYY-MM-DD and HH:mm formats)
                if (quoteData.commencementDate) {
                    const commenceDate = new Date(quoteData.commencementDate);
                    const year = commenceDate.getFullYear();
                    const month = String(commenceDate.getMonth() + 1).padStart(2, "0");
                    const day = String(commenceDate.getDate()).padStart(2, "0");
                    setCommencementDate(`${year}-${month}-${day}`);
                }
                if (quoteData.commencementTime) {
                    setCommencementTime(quoteData.commencementTime);
                }
                // Parse and set completion date/time (convert to YYYY-MM-DD and HH:mm formats)
                if (quoteData.completionDate) {
                    const completeDate = new Date(quoteData.completionDate);
                    const year = completeDate.getFullYear();
                    const month = String(completeDate.getMonth() + 1).padStart(2, "0");
                    const day = String(completeDate.getDate()).padStart(2, "0");
                    setCompletionDate(`${year}-${month}-${day}`);
                }
                if (quoteData.completionTime) {
                    setCompletionTime(quoteData.completionTime);
                }
                // Set project duration
                if (quoteData.projectDuration) {
                    setProjectDuration(quoteData.projectDuration.toString());
                }
                // Set budget from totalAmount
                if (quoteData.totalAmount) {
                    const budget = quoteData.totalAmount;
                    if (budget <= 10000) {
                        setBudgetOption("10K");
                    }
                    else if (budget <= 20000) {
                        setBudgetOption("20K");
                    }
                    else if (budget <= 50000) {
                        setBudgetOption("50K");
                    }
                    else {
                        setBudgetOption("Other");
                        setCustomBudget(budget.toString());
                    }
                }
            }
            catch (err) {
                console.error("Error fetching quote:", err);
                setError("Failed to load quote. Please try again.");
                if (err?.response?.status === 404) {
                    setError("Quote not found.");
                }
                else if (err?.response?.status === 403 ||
                    err?.response?.status === 401) {
                    setError("You don't have permission to view this quote.");
                }
                showError("Failed to load quote. Please try again.");
            }
            finally {
                setLoading(false);
            }
        };
        fetchQuote();
    }, [quoteId, showError]);
    // Clients can only view, never edit
    const isEditable = false;
    const handleBack = () => {
        navigate("/client/quotes");
    };
    // Loading state
    if (loading) {
        return (_jsxs("div", { className: styles.page, children: [_jsx("header", { className: styles.pageHeader, children: _jsxs("div", { children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: "\u2190 Back to Quotes" }), _jsx("h1", { className: styles.title, children: "QUOTE DETAILS" })] }) }), _jsx("div", { className: styles.content, children: _jsx("p", { children: "Loading quote data..." }) })] }));
    }
    // Error state
    if (error) {
        return (_jsxs("div", { className: styles.page, children: [_jsx("header", { className: styles.pageHeader, children: _jsxs("div", { children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: "\u2190 Back to Quotes" }), _jsx("h1", { className: styles.title, children: "QUOTE DETAILS" })] }) }), _jsxs("div", { className: styles.content, children: [_jsx("p", { children: error }), _jsx("button", { onClick: () => navigate("/client/quotes"), style: {
                                marginTop: "var(--space-md)",
                                padding: "10px 20px",
                                border: "none",
                                borderRadius: "var(--radius-md)",
                                background: "var(--color-primary)",
                                color: "white",
                                cursor: "pointer",
                            }, children: "Back to Quotes" })] })] }));
    }
    const handleOpenUploadModal = () => setIsUploadModalOpen(true);
    const handleCloseUploadModal = () => setIsUploadModalOpen(false);
    const handleUploadSave = async (files, modalCategory) => {
        if (!quoteId || !quote) {
            showError("Quote not loaded. Please refresh the page.");
            return;
        }
        const categoryMap = {
            Permits: "permits",
            Plans: "plans",
            Other: "other",
        };
        const documentCategory = categoryMap[modalCategory] || "other";
        setIsSavingDocuments(true);
        try {
            for (const fileInfo of files) {
                if (!fileInfo.file) {
                    throw new Error(`File object missing for ${fileInfo.name}`);
                }
                const name = fileInfo.name.split(".")[0] || fileInfo.name;
                await documentsService.uploadQuoteDocument(fileInfo.file, {
                    quoteId,
                    name,
                    quoteCategory: documentCategory,
                });
            }
            await fetchDocuments();
            setIsUploadModalOpen(false);
            showSuccess(`${files.length} document${files.length > 1 ? "s" : ""} uploaded successfully`);
        }
        catch (err) {
            console.error("Error uploading documents:", err);
            const message = err instanceof Error ? err.message : "Failed to upload documents";
            showError(message);
        }
        finally {
            setIsSavingDocuments(false);
        }
    };
    const handleDownloadJobDocument = async (doc) => {
        try {
            const response = await documentsService.getDownloadUrl(doc.id);
            if (response.data?.url) {
                window.open(response.data.url, "_blank", "noopener,noreferrer");
            }
        }
        catch (err) {
            console.error("Error downloading document:", err);
            showError("Failed to download document.");
        }
    };
    const handleDeleteJobDocument = async (docId) => {
        try {
            await documentsService.deleteDocument(docId);
            await fetchDocuments();
            showSuccess("Document removed successfully");
        }
        catch (err) {
            console.error("Error deleting document:", err);
            showError("Failed to delete document.");
        }
    };
    return (_jsxs("div", { className: styles.page, children: [_jsx("header", { className: styles.pageHeader, children: _jsxs("div", { children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: "\u2190 Back to Quotes" }), _jsx("h1", { className: styles.title, children: isEditable ? "QUOTE DETAILS" : "VIEW QUOTE REQUEST" })] }) }), _jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, children: TABS.map((tab) => (_jsx("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), children: tab.label }, tab.id))) }) }), _jsxs("div", { className: styles.content, children: [activeTab === "site_particulars" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.twoColumnGrid, children: [_jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Location" }), _jsx("input", { type: "text", className: "form-input", value: location, onChange: (e) => setLocation(e.target.value), readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Suburb" }), isEditable ? (_jsxs("div", { ref: suburbDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", value: suburbSearchTerm, onChange: (e) => {
                                                                        const newValue = e.target.value;
                                                                        setSuburbSearchTerm(newValue);
                                                                        setShowSuburbDropdown(true);
                                                                        setSuburb(newValue);
                                                                    }, onFocus: () => setShowSuburbDropdown(true), placeholder: "Search suburbs..." }), suburbSearchTerm && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearSuburb, children: "\u00D7" }))] }), showSuburbDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredSuburbs.length === 0 && suburbSearchTerm ? (_jsx("div", { className: styles.dropdownEmpty, children: "No suburbs found" })) : filteredSuburbs.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: "Start typing to search suburbs..." })) : (filteredSuburbs.map((suburbOption, index) => (_jsx("button", { type: "button", className: `${styles.contactOption} ${suburb === suburbOption.name
                                                                    ? styles.selected
                                                                    : ""}`, onClick: () => handleSuburbSelect(suburbOption), children: _jsxs("span", { className: styles.contactName, children: [suburbOption.name, ", ", suburbOption.state, " ", suburbOption.postcode] }) }, `${suburbOption.name}-${suburbOption.state}-${index}`)))) }))] })) : (_jsx("input", { type: "text", className: "form-input", value: suburb, readOnly: true, disabled: true }))] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "State" }), _jsxs("select", { className: "form-select", value: state, onChange: (e) => setState(e.target.value), disabled: !isEditable, children: [_jsx("option", { value: "", children: "Select State" }), AUSTRALIAN_STATES.map((s) => (_jsx("option", { value: s.value, children: s.label }, s.value)))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Postcode" }), _jsx("input", { type: "text", className: "form-input", value: postcode, onChange: (e) => handleNumberInput(e, setPostcode), maxLength: 4, readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site Name" }), _jsx("input", { type: "text", className: "form-input", value: siteName, onChange: (e) => setSiteName(e.target.value), readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Name" }), _jsx("input", { type: "text", className: "form-input", value: tenancyName, onChange: (e) => setTenancyName(e.target.value), readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Number" }), _jsx("input", { type: "text", className: "form-input", value: tenancyNumber, onChange: (e) => setTenancyNumber(e.target.value), readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building Type" }), _jsxs("select", { className: "form-select", value: buildingType, onChange: (e) => setBuildingType(e.target.value), disabled: !isEditable, children: [_jsx("option", { value: "", children: "Select Building Type" }), BUILDING_TYPES.map((type) => (_jsx("option", { value: type.value, children: type.label }, type.value)))] })] })] }), _jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Square Meterage" }), _jsx("input", { type: "text", className: "form-input", value: squareMeterage, onChange: (e) => handleNumberInput(e, setSquareMeterage), readOnly: !isEditable, disabled: !isEditable })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site Inspection Available" }), _jsx("div", { className: styles.toggleWrapper, children: _jsxs("button", { className: `${styles.toggle} ${siteInspectionAvailable ? styles.toggleActive : ""}`, onClick: () => isEditable &&
                                                            setSiteInspectionAvailable(!siteInspectionAvailable), disabled: !isEditable, children: [_jsx("span", { className: styles.toggleLabel, children: siteInspectionAvailable ? "YES" : "NO" }), _jsx("span", { className: styles.toggleSwitch })] }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building/Demolition Permit Required" }), _jsx("div", { className: styles.toggleWrapper, children: _jsxs("button", { className: `${styles.toggle} ${permitRequired ? styles.toggleActive : ""}`, onClick: () => isEditable && setPermitRequired(!permitRequired), disabled: !isEditable, children: [_jsx("span", { className: styles.toggleLabel, children: permitRequired ? "YES" : "NO" }), _jsx("span", { className: styles.toggleSwitch })] }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Type" }), _jsxs("div", { className: styles.defitToggleWrapper, children: [_jsx("span", { className: `${styles.defitOption} ${defitType === "full" ? styles.defitOptionActive : ""}`, children: "Full Defit" }), _jsx("button", { className: `${styles.defitToggle} ${defitType === "partial" ? styles.defitToggleActive : ""}`, onClick: () => isEditable &&
                                                                setDefitType(defitType === "full" ? "partial" : "full"), disabled: !isEditable, children: _jsx("span", { className: styles.defitToggleSwitch }) }), _jsx("span", { className: `${styles.defitOption} ${defitType === "partial" ? styles.defitOptionActive : ""}`, children: "Partial Defit" })] })] }), _jsxs("div", { className: `${styles.formGroupVertical} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Scope of Works" }), _jsx("textarea", { className: "form-textarea", rows: 10, value: scopeOfWorks, onChange: (e) => setScopeOfWorks(e.target.value), readOnly: !isEditable, disabled: !isEditable })] })] })] }) })), activeTab === "job_description" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.twoColumnGrid, children: [_jsxs("div", { className: styles.paper, children: [_jsx("div", { className: styles.dateTimeSection, children: _jsxs("div", { className: styles.dateTimeRow, children: [_jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Commencement Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: "form-input", value: commencementDate, onChange: setCommencementDate, disabled: !isEditable }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: commencementTime, onChange: (e) => setCommencementTime(e.target.value), readOnly: !isEditable, disabled: !isEditable }) })] }) }), _jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Completion Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: "form-input", value: completionDate, onChange: setCompletionDate, readOnly: !isEditable, disabled: !isEditable, onInvalid: () => showError("Please enter a valid date.") }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: completionTime, onChange: (e) => setCompletionTime(e.target.value), readOnly: !isEditable, disabled: !isEditable }) })] }) })] }) }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Project Duration (days)" }), _jsx("input", { type: "text", className: styles.durationInput, value: projectDuration, onChange: (e) => setProjectDuration(e.target.value), readOnly: !isEditable, disabled: !isEditable })] })] }), _jsxs("div", { className: styles.paper, children: [_jsxs("div", { className: `${styles.formGroupVertical} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Estimated Budget" }), _jsx("div", { className: styles.budgetOptions, children: ["10K", "20K", "50K", "Other"].map((option) => (_jsx("button", { type: "button", className: `${styles.budgetButton} ${budgetOption === option
                                                            ? styles.budgetButtonActive
                                                            : ""}`, onClick: () => isEditable && setBudgetOption(option), disabled: !isEditable, children: option }, option))) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Enter Budget" }), _jsx("input", { type: "text", className: "form-input", value: customBudget, onChange: (e) => setCustomBudget(e.target.value), readOnly: !isEditable, disabled: !isEditable })] })] })] }) })), activeTab === "documents" && (_jsx("div", { className: styles.tabContent, children: _jsxs("div", { className: styles.jobDocSection, children: [_jsxs("div", { className: styles.jobDocHeader, children: [_jsx("div", { className: styles.jobDocFilterTabs, children: JOB_DESCRIPTION_FILTERS.map((filter) => (_jsx("button", { type: "button", className: `${styles.jobDocFilterTab} ${jobDescriptionFilter === filter.id
                                                    ? styles.jobDocFilterTabActive
                                                    : ""}`, onClick: () => setJobDescriptionFilter(filter.id), children: _jsx("span", { children: filter.label }) }, filter.id))) }), _jsxs("div", { className: styles.jobDocHeaderActions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsx("button", { type: "button", className: styles.addDocsButton, onClick: handleOpenUploadModal, disabled: !isEditable, children: "Add Documents" })] })] }), _jsx("div", { className: styles.jobDocTableWrapper, children: _jsxs("table", { className: styles.jobDocTable, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.jobDocTableHeader, children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Category" }), _jsx("th", { children: "Date" }), _jsx("th", { children: "Uploaded By" }), _jsx("th", { children: "Actions" })] }) }), _jsx("tbody", { children: documentsLoading ? (_jsx("tr", { children: _jsx("td", { colSpan: 5, className: styles.jobDocEmptyState, children: "Loading documents..." }) })) : filteredJobDescriptionDocs.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 5, className: styles.jobDocEmptyState, children: "No documents stored yet" }) })) : (filteredJobDescriptionDocs.map((doc) => (_jsxs("tr", { className: styles.jobDocTableRow, children: [_jsx("td", { className: styles.jobDocTableCell, children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.name, children: doc.name }), _jsx("span", { className: styles.meta, children: getJobDescriptionFilterLabel(doc.category) })] }) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("span", { className: styles.categoryLabel, children: getJobDescriptionFilterLabel(doc.category) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("span", { className: styles.date, children: formatDocDate(doc.date) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsx("div", { className: styles.uploadedBy, children: _jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: (doc.uploadedBy || "U").charAt(0) }), _jsx("span", { className: styles.userName, children: doc.uploadedBy })] }) }) }), _jsx("td", { className: styles.jobDocTableCell, children: _jsxs("div", { className: styles.jobDocActions, children: [_jsx("button", { className: styles.jobDocActionButton, onClick: () => handleDownloadJobDocument(doc), title: "Download", type: "button", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M12 5v10" }), _jsx("path", { d: "M8 15l4 4 4-4" }), _jsx("path", { d: "M4 19h16" })] }) }), _jsx("button", { className: styles.jobDocDeleteButton, onClick: () => handleDeleteJobDocument(doc.id), title: "Delete", type: "button", disabled: !isEditable, children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" }), _jsx("line", { x1: "6", y1: "18", x2: "18", y2: "6" })] }) })] }) })] }, doc.id)))) })] }) })] }) }))] }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: handleCloseUploadModal, onSave: handleUploadSave, categories: ["Permits", "Plans", "Other"], isUploading: isSavingDocuments, uploadButtonText: "Uploading..." })] }));
};
